import React, { useContext, useEffect, useReducer, useState } from "react";

import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Tooltip,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import { corDoTextoDaEtiqueta } from "../../utils/estiloAstro";
import { bannerNavy, bannerTitulo, bannerSub, botaoVerdeBrilho, campoNavy } from "../../utils/estiloNavy";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit, AddRounded, LocalOfferRounded } from "@material-ui/icons";
import TagModal from "../../components/TagModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  return {
    pagina: {
      padding: theme.spacing(2.5, 2, 4),
    },
    // cabeçalho verde com título, contador e botão
    topo: {
      ...bannerNavy(theme),
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 16,
      marginBottom: theme.spacing(2),
    },
    topoIcone: {
      width: 44,
      height: 44,
      borderRadius: 13,
      display: "grid",
      placeItems: "center",
      background: "linear-gradient(135deg,#22c55e,#15803d)",
      boxShadow: "0 6px 18px rgba(34,197,94,.35)",
      marginRight: 14,
      flexShrink: 0,
    },
    topoTitulo: bannerTitulo,
    topoSub: bannerSub,
    contador: {
      display: "inline-block",
      marginLeft: 4,
      padding: "2px 10px",
      borderRadius: 20,
      fontSize: "0.8rem",
      fontWeight: 800,
      color: "#4ade80",
      background: "rgba(74,222,128,.14)",
      verticalAlign: "middle",
    },
    botaoAdicionar: botaoVerdeBrilho,
    busca: {
      ...campoNavy(theme),
      width: 320,
      maxWidth: "100%",
      marginBottom: theme.spacing(2),
    },

    // grade de cartões
    grade: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
      gap: 16,
    },
    cartao: {
      position: "relative",
      overflow: "hidden",
      minWidth: 0, // nome comprido não alarga a coluna da grade
      display: "flex",
      flexDirection: "column",
      gap: 14,
      padding: "18px 18px 14px 22px",
      borderRadius: 18,
      backgroundColor: theme.palette.background.paper,
      // brilho suave com a cor da etiqueta no topo do cartão
      backgroundImage: "radial-gradient(260px 90px at 0% 0%, var(--suave), transparent 75%)",
      border: escuro ? "1px solid rgba(74,222,128,.10)" : "1px solid #e3ece6",
      boxShadow: escuro ? "0 8px 22px rgba(0,0,0,.3)" : "0 4px 14px rgba(16,40,28,.06)",
      transition: "transform .2s ease, box-shadow .2s ease",
      animation: "iaSubir .35s ease backwards",
      // faixa com a cor da etiqueta na lateral
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 5,
        background: "var(--cor)",
      },
      "&:hover": {
        transform: "translateY(-3px)",
        borderColor: "var(--borda)",
        boxShadow: escuro ? "0 14px 30px rgba(0,0,0,.45)" : "0 14px 30px var(--suave)",
      },
      "&:hover $acoes": { opacity: 1 },
    },
    // como a etiqueta aparece nos atendimentos
    pilula: {
      alignSelf: "flex-start",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      maxWidth: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      padding: "5px 12px",
      borderRadius: 20,
      fontSize: "0.85rem",
      fontWeight: 700,
      color: "var(--texto)",
      background: "var(--cor)",
      boxShadow: "0 4px 12px var(--sombra)",
      overflow: "hidden",
      "& svg": { flexShrink: 0 },
    },
    // o nome corta com "..." em vez de vazar do cartão
    pilulaNome: {
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    rodape: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      borderTop: `1px dashed ${theme.palette.divider}`,
      paddingTop: 10,
    },
    hex: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: "0.76rem",
      fontWeight: 600,
      color: theme.palette.text.secondary,
      fontFamily: "monospace",
      textTransform: "uppercase",
    },
    bolinha: {
      width: 14,
      height: 14,
      borderRadius: "50%",
      background: "var(--cor)",
      boxShadow: `0 0 0 3px ${escuro ? "rgba(255,255,255,.06)" : "#f1f5f3"}`,
    },
    acoes: {
      display: "flex",
      gap: 4,
      opacity: 0.75,
      transition: "opacity .2s ease",
    },
    botaoAcao: {
      width: 32,
      height: 32,
      borderRadius: 9,
      color: theme.palette.text.secondary,
      backgroundColor: escuro ? "rgba(255,255,255,.04)" : "#f3f7f4",
      "&:hover": { color: "#1E9855", backgroundColor: "rgba(30,152,85,.12)" },
    },
    botaoExcluir: {
      "&:hover": { color: "#dc2626 !important", backgroundColor: "rgba(220,38,38,.10) !important" },
    },

    // vazio / carregando
    vazio: {
      gridColumn: "1 / -1",
      textAlign: "center",
      padding: "48px 16px",
      borderRadius: 16,
      border: `2px dashed ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
      "& b": { display: "block", color: theme.palette.text.primary, fontSize: "1rem", marginBottom: 4 },
    },
    esqueleto: {
      height: 118,
      borderRadius: 16,
      background: escuro
        ? "linear-gradient(90deg, rgba(255,255,255,.03), rgba(255,255,255,.07), rgba(255,255,255,.03))"
        : "linear-gradient(90deg, #eef3f0, #f7faf8, #eef3f0)",
      backgroundSize: "200% 100%",
      animation: "$brilho 1.2s linear infinite",
    },
    "@keyframes brilho": {
      from: { backgroundPosition: "200% 0" },
      to: { backgroundPosition: "-200% 0" },
    },
  };
});

const hexParaRgba = (hex, alfa) => {
  const h = String(hex || "").replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(h)) return `rgba(30,152,85,${alfa})`;
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alfa})`;
};

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];

    tags.forEach((tag) => {
      const tagIndex = state.findIndex((q) => q.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
      } else {
        newTags.push(tag);
      }
    });

    return [...state, ...newTags];
  }

  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((u) => u.id === tag.id);

    if (tagIndex !== -1) {
      state[tagIndex] = tag;
      return [...state];
    } else {
      return [tag, ...state];
    }
  }

  if (action.type === "DELETE_TAG") {
    const tagId = action.payload;
    const tagIndex = state.findIndex((q) => q.id === tagId);
    if (tagIndex !== -1) {
      state.splice(tagIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Tags = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [tags, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const termo = busca.trim().toLowerCase();
  const tagsVisiveis = termo ? tags.filter((t) => String(t.name || "").toLowerCase().includes(termo)) : tags;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tags");
        dispatch({ type: "LOAD_TAGS", payload: data });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    const onTag = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAG", payload: data.tagId });
      }
    };
    socket.on("tag", onTag);

    return () => {
      socket.off("tag", onTag);
    };
  }, []);

  const handleOpenTagModal = () => {
    setTagModalOpen(true);
    setSelectedTag(null);
  };

  const handleCloseTagModal = () => {
    setTagModalOpen(false);
    setSelectedTag(null);
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedTag(null);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success("Etiqueta excluída com sucesso!");
    } catch (err) {
      toastError(err);
    }
    setSelectedTag(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedTag &&
          `Excluir ${selectedTag.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteTag(selectedTag.id)}
      >
        Tem certeza que deseja excluir esta etiqueta?
      </ConfirmationModal>
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        tagId={selectedTag?.id}
      />
      <div className={classes.pagina}>
        <div className={classes.topo}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className={classes.topoIcone}>
              <LocalOfferRounded />
            </div>
            <div>
              <div className={classes.topoTitulo}>
                Etiquetas
                <span className={classes.contador}>{tags.length}</span>
              </div>
              <div className={classes.topoSub}>
                Organize seus atendimentos com etiquetas coloridas
              </div>
            </div>
          </div>
          <Can
            role={user.profile}
            perform="tags-page:manage"
            yes={() => (
              <Button
                className={classes.botaoAdicionar}
                startIcon={<AddRounded />}
                onClick={handleOpenTagModal}
              >
                Nova etiqueta
              </Button>
            )}
          />
        </div>

        {tags.length > 6 && (
          <TextField
            className={classes.busca}
            variant="outlined"
            size="small"
            placeholder="Buscar etiqueta"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
        )}

        <div className={classes.grade}>
          {tagsVisiveis.map((tag, i) => (
            <div
              key={tag.id}
              className={classes.cartao}
              style={{
                "--cor": tag.color,
                "--sombra": hexParaRgba(tag.color, 0.3),
                "--suave": hexParaRgba(tag.color, 0.12),
                "--borda": hexParaRgba(tag.color, 0.45),
                "--texto": corDoTextoDaEtiqueta(tag.color),
                animationDelay: `${Math.min(i, 12) * 0.04}s`,
              }}
            >
              <span className={classes.pilula} title={tag.name}>
                <LocalOfferRounded style={{ fontSize: 15 }} />
                <span className={classes.pilulaNome}>{tag.name}</span>
              </span>
              <div className={classes.rodape}>
                <span className={classes.hex}>
                  <span className={classes.bolinha} />
                  {tag.color}
                </span>
                <Can
                  role={user.profile}
                  perform="tags-page:manage"
                  yes={() => (
                    <div className={classes.acoes}>
                      <Tooltip title="Editar" arrow>
                        <IconButton
                          size="small"
                          className={classes.botaoAcao}
                          onClick={() => handleEditTag(tag)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir" arrow>
                        <IconButton
                          size="small"
                          className={`${classes.botaoAcao} ${classes.botaoExcluir}`}
                          onClick={() => {
                            setSelectedTag(tag);
                            setConfirmModalOpen(true);
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                />
              </div>
            </div>
          ))}

          {loading &&
            [0, 1, 2].map((n) => <div key={n} className={classes.esqueleto} />)}

          {!loading && tags.length > 0 && tagsVisiveis.length === 0 && (
            <div className={classes.vazio}>
              <b>Nenhuma etiqueta com esse nome</b>
              Tente outra busca.
            </div>
          )}

          {!loading && tags.length === 0 && (
            <div className={classes.vazio}>
              <b>Nenhuma etiqueta ainda</b>
              Crie a primeira em "Nova etiqueta" para organizar seus atendimentos.
            </div>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default Tags;
